import browser from "@raiden/library/constants/browser";
import { POSTS_TYPE_VALUE_ARTICLE } from "@raiden/library/constants/posts";
import { RESOURCES_VISIBILITY_VALUE_PUBLIC } from "@raiden/library/constants/resources";
import { usePreferences } from "@raiden/library/contexts/Preferences";
import { useApi } from "@raiden/library/hooks/useApi";
import { serverApiFetcher } from "@raiden/library/hooks/useApiFetcher";
import { useConfiguration } from "@raiden/library/hooks/useConfiguration";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import generateFrontUri from "@raiden/library/libraries/utils/generateFrontUrl";
import generatePampaUrl from "@raiden/library/libraries/utils/generatePampaUrl";
import { useMemo } from "react";
import Seo from "../../components/Seo";
import { References } from "../../containers/References";

const ALIAS_POST_REFERENCES = process.env.NEXT_PUBLIC_POST_ALIAS_REFERENCES;
const ALIAS_POST_CATEGORY_REVIEW =
  process.env.NEXT_PUBLIC_POST_CATEGORY_ALIAS_REVIEW;

/**
 * @param {object} params
 * @param {number | string} params.postId
 * @returns {string}
 */
function getPostUrl({ postId }) {
  return generateApiUrl({
    id: "@api.posts.view",
    parameters: { postId },
    query: {
      fields: ["post.current_revision", "post.revision.cover"],
    },
  });
}

/**
 * @param {object} params
 * @param {number} params.environmentId
 * @param {number[] | string[]} [params.categoryIds]
 * @returns {string}
 */
function getPostReviewUrl({ environmentId, categoryIds }) {
  return generateApiUrl({
    id: "@api.posts.viewAny",
    query: {
      environment_id: [environmentId],
      type: [POSTS_TYPE_VALUE_ARTICLE],
      visibility: RESOURCES_VISIBILITY_VALUE_PUBLIC,
      category_id: categoryIds,
      page: 1,
      per_page: 6,
      fields: ["post.current_revision"],
    },
  });
}

/**
 * @typedef {object} Props
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post>} [postResponse]
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post>} [postReviewResponse]
 */
/**
 * @param {Props} props
 */
function Page({ postResponse, postReviewResponse }) {
  const translate = useTranslate();

  const { bestEnvironment } = usePreferences();

  const { configuration } = useConfiguration();

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Post").Post>} */
  const { swrResponse: swrResponsePost } = useApi(
    postResponse === undefined && ALIAS_POST_REFERENCES !== undefined
      ? getPostUrl({ postId: ALIAS_POST_REFERENCES })
      : null,
    {
      swrConfig: {
        fallbackData: postResponse,
      },
    },
  );

  const postCategories = useMemo(() => {
    return configuration?.posts_categories?.article?.filter(
      (category) => category.alias === ALIAS_POST_CATEGORY_REVIEW,
    );
  }, [configuration?.posts_categories?.article]);

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Post").Post[]>} */
  const { swrResponse: swrResponsePostArticleReviews } = useApi(
    postReviewResponse === undefined &&
      ALIAS_POST_CATEGORY_REVIEW !== undefined &&
      bestEnvironment
      ? getPostReviewUrl({
          environmentId: bestEnvironment.id,
          categoryIds:
            (postCategories?.length ?? 0) > 0
              ? postCategories?.map((category) => category.id)
              : [],
        })
      : null,
    {
      swrConfig: {
        fallbackData: postReviewResponse,
      },
    },
  );

  const post = swrResponsePost.data?.data;
  const currentRevision = post?.current_revision;
  const title = translate(currentRevision?.seo_title ?? currentRevision?.title);
  const description = translate(
    currentRevision?.seo_description ?? currentRevision?.summary,
  );
  const cover = currentRevision?.cover;
  const imageUrl = cover
    ? generatePampaUrl({
        image: cover,
        mode: "crop",
        format: "default",
      })
    : undefined;

  return (
    <>
      <Seo
        title={title}
        description={description}
        ogType="article"
        imageUrl={imageUrl}
        links={(locale) =>
          generateFrontUri({
            id: "@front.notreRole",
            parameters: {
              locale,
            },
          })
        }
        structuredData={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: title,
          image: imageUrl ? [imageUrl] : [],
          datePublished: post?.published_at,
          dateModified: post?.updated_at,
        }}
      />

      <References
        swrResponsePost={swrResponsePost}
        swrResponsePostArticleReviews={swrResponsePostArticleReviews}
      />
    </>
  );
}

export default Page;

/**
 * @typedef {import("../../types/Page").PageInitialProps & Props} InitialProps
 */
/**
 * @param {import("../../types/AppContext").PageContext} context
 * @returns {Promise<InitialProps>}
 */
Page.getInitialProps = async function (context) {
  /** @type {InitialProps} */
  const values = {};

  const reviewAlias = process.env.NEXT_PUBLIC_POST_CATEGORY_ALIAS_REVIEW;

  if (!browser) {
    if (ALIAS_POST_REFERENCES !== undefined) {
      values.postResponse = await serverApiFetcher(
        getPostUrl({ postId: ALIAS_POST_REFERENCES }),
      ).catch((error) => {
        console.error("Failed to fetch post response", error);
      });
    }

    const bestEnvironment =
      context.configuration?.environments?.length === 1
        ? context.configuration.environments[0]
        : undefined;

    const postReviewAliasPage =
      context.configuration?.posts_categories?.article?.find(
        (category) => category.alias === reviewAlias,
      );

    if (ALIAS_POST_CATEGORY_REVIEW !== undefined && bestEnvironment) {
      values.postReviewResponse = await serverApiFetcher(
        getPostReviewUrl({
          categoryIds: postReviewAliasPage ? [postReviewAliasPage.id] : [],
          environmentId: bestEnvironment.id,
        }),
      ).catch((error) => {
        console.error("Failed to fetch post review response", error);
      });
    }
  }

  return values;
};
